import { supabase } from '../lib/supabase.js';

// Enhanced Event service with improved error handling and retry logic
export class EventService {
  
  // Retry mechanism for network failures
  static async withRetry(operation, maxRetries = 3, delay = 1000) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await operation();
        return result;
      } catch (error) {
        console.warn(`Attempt ${attempt} failed:`, error?.message);
        
        // If it's the last attempt or not a network error, throw
        if (attempt === maxRetries || !this.isNetworkError(error)) {
          throw error;
        }
        
        // Wait before retrying with exponential backoff
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    }
  }

  // Check if error is network-related
  static isNetworkError(error) {
    const networkErrors = ['Failed to fetch', 'NetworkError', 'fetch'];
    return networkErrors?.some(term => 
      error?.message?.includes(term) || error?.toString()?.includes(term)
    );
  }

  // Enhanced connection check
  static async checkConnection() {
    try {
      const { data, error } = await supabase?.from('events')?.select('id')?.limit(1);
      return { connected: !error, error };
    } catch (error) {
      return { connected: false, error: error?.message };
    }
  }

  // Get all events with enhanced error handling and retry logic
  static async getAllEvents(filters = {}) {
    try {
      // Check connection first
      const connectionCheck = await this.checkConnection();
      if (!connectionCheck?.connected) {
        console.error('Supabase connection failed:', connectionCheck?.error);
        return { 
          data: [], 
          error: 'Unable to connect to database. Please check your connection.', 
          count: 0 
        };
      }

      const result = await this.withRetry(async () => {
        let query = supabase?.from('events')?.select(`
          *,
          host:user_profiles!events_host_id_fkey(
            id,
            full_name,
            email,
            avatar_url
          ),
          event_tickets(
            id,
            ticket_name,
            ticket_type,
            price,
            quantity_available,
            quantity_sold,
            description
          )
        `);

        // Apply status filter (default to published for public users)
        if (filters?.status) {
          query = query?.eq('status', filters?.status);
        } else {
          query = query?.eq('status', 'published');
        }

        // Apply category filter - use actual database enum values
        if (filters?.category && filters?.category !== 'all') {
          query = query?.eq('category', filters?.category);
        }

        // Apply location filter
        if (filters?.location && filters?.location !== 'all') {
          query = query?.ilike('location', `%${filters?.location}%`);
        }

        // Apply search query
        if (filters?.search) {
          query = query?.or(`title.ilike.%${filters?.search}%,description.ilike.%${filters?.search}%,location.ilike.%${filters?.search}%`);
        }

        // Apply date filtering
        if (filters?.dateFrom) {
          query = query?.gte('event_date', filters?.dateFrom);
        }
        if (filters?.dateTo) {
          query = query?.lte('event_date', filters?.dateTo);
        }

        // Apply price range filtering
        if (filters?.minPrice !== undefined || filters?.maxPrice !== undefined) {
          // This would require a more complex query with joins to event_tickets
          // For now, we'll fetch all and filter in JavaScript
        }

        // Apply sorting
        const sortBy = filters?.sortBy || 'created_at';
        const sortOrder = filters?.sortOrder || 'desc';
        query = query?.order(sortBy, { ascending: sortOrder === 'asc' });

        // Apply pagination
        if (filters?.page && filters?.pageSize) {
          const from = (filters?.page - 1) * filters?.pageSize;
          const to = from + filters?.pageSize - 1;
          query = query?.range(from, to);
        }

        return await query;
      });

      const { data, error, count } = result;

      if (error) {
        console.error('Error fetching events:', error);
        return { data: [], error: error?.message, count: 0 };
      }

      // Transform data to match expected format
      let transformedData = data?.map(event => ({
        id: event?.id,
        title: event?.title,
        description: event?.description,
        image: event?.image_url,
        date: event?.event_date,
        endDate: event?.end_date,
        location: event?.location,
        venue_name: event?.venue_name,
        category: event?.category,
        status: event?.status,
        maxCapacity: event?.max_capacity,
        isFeatured: event?.is_featured,
        organizer: event?.host?.full_name || 'Event Organizer',
        organizerEmail: event?.host?.email,
        organizerAvatar: event?.host?.avatar_url,
        hostId: event?.host_id,
        createdAt: event?.created_at,
        updatedAt: event?.updated_at,
        tiers: event?.event_tickets?.map(ticket => ({
          id: ticket?.id,
          name: ticket?.ticket_name,
          type: ticket?.ticket_type,
          price: parseFloat(ticket?.price || 0),
          description: ticket?.description,
          quantityAvailable: ticket?.quantity_available,
          quantitySold: ticket?.quantity_sold
        })) || [],
        // Calculate attendance info
        attendeeCount: event?.event_tickets?.reduce((sum, ticket) => sum + (ticket?.quantity_sold || 0), 0) || 0,
        // Calculate price range
        priceRange: event?.event_tickets?.length > 0 ? {
          min: Math.min(...event?.event_tickets?.map(t => parseFloat(t?.price || 0))),
          max: Math.max(...event?.event_tickets?.map(t => parseFloat(t?.price || 0)))
        } : { min: 0, max: 0 }
      })) || [];

      return { data: transformedData, error: null, count: count || transformedData?.length };
    } catch (error) {
      console.error('Unexpected error in getAllEvents:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Unexpected error occurred';
      if (this.isNetworkError(error)) {
        errorMessage = 'Network connection failed. Please check your internet connection and try again.';
      } else if (error?.message?.includes('auth')) {
        errorMessage = 'Authentication error. Please refresh and try again.';
      }
      
      return { data: [], error: errorMessage, count: 0 };
    }
  }

  // Get featured events with enhanced error handling
  static async getFeaturedEvents(limit = 6) {
    try {
      const result = await this.withRetry(async () => {
        return await supabase?.from('events')?.select(`
          *,
          host:user_profiles!events_host_id_fkey(
            id,
            full_name,
            email,
            avatar_url
          ),
          event_tickets(
            id,
            ticket_name,
            ticket_type,
            price,
            quantity_available,
            quantity_sold,
            description
          )
        `)
          ?.eq('status', 'published')
          ?.eq('is_featured', true)
          ?.order('created_at', { ascending: false })
          ?.limit(limit);
      });

      const { data, error } = result;

      if (error) {
        console.error('Error fetching featured events:', error);
        return { data: [], error: error?.message };
      }

      // Transform data
      let transformedData = data?.map(event => ({
        id: event?.id,
        title: event?.title,
        description: event?.description,
        image: event?.image_url,
        date: event?.event_date,
        endDate: event?.end_date,
        location: event?.location,
        venue_name: event?.venue_name,
        category: event?.category,
        status: event?.status,
        maxCapacity: event?.max_capacity,
        isFeatured: event?.is_featured,
        organizer: event?.host?.full_name || 'Event Organizer',
        organizerEmail: event?.host?.email,
        organizerAvatar: event?.host?.avatar_url,
        hostId: event?.host_id,
        createdAt: event?.created_at,
        updatedAt: event?.updated_at,
        tiers: event?.event_tickets?.map(ticket => ({
          id: ticket?.id,
          name: ticket?.ticket_name,
          type: ticket?.ticket_type,
          price: parseFloat(ticket?.price || 0),
          description: ticket?.description,
          quantityAvailable: ticket?.quantity_available,
          quantitySold: ticket?.quantity_sold
        })) || [],
        attendeeCount: event?.event_tickets?.reduce((sum, ticket) => sum + (ticket?.quantity_sold || 0), 0) || 0
      })) || [];

      return { data: transformedData, error: null };
    } catch (error) {
      console.error('Unexpected error in getFeaturedEvents:', error);
      
      let errorMessage = 'Failed to load featured events';
      if (this.isNetworkError(error)) {
        errorMessage = 'Network connection failed while loading featured events. Please try again.';
      }
      
      return { data: [], error: errorMessage };
    }
  }

  // Get single event by ID (READ operation) - Add enhanced validation
  static async getEventById(eventId) {
    try {
      // Validate UUID format before making database call
      if (!eventId) {
        return { data: null, error: 'Event ID is required' };
      }

      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex?.test(eventId)) {
        return { data: null, error: 'Invalid event ID format' };
      }

      const { data, error } = await supabase?.from('events')?.select(`
        *,
        host:user_profiles!events_host_id_fkey(
          id,
          full_name,
          email,
          avatar_url
        ),
        event_tickets(
          id,
          ticket_name,
          ticket_type,
          price,
          quantity_available,
          quantity_sold,
          description,
          sale_start_date,
          sale_end_date
        )
      `)?.eq('id', eventId)?.single();

      if (error) {
        console.error('Error fetching event by ID:', error);
        
        // Provide more specific error messages
        if (error?.code === '22P02') {
          return { data: null, error: 'Invalid event ID format - expected UUID format' };
        }
        
        return { data: null, error: error?.message };
      }

      if (!data) {
        return { data: null, error: 'Event not found' };
      }

      // Transform data
      let transformedData = {
        id: data?.id,
        title: data?.title,
        description: data?.description,
        image: data?.image_url,
        date: data?.event_date,
        endDate: data?.end_date,
        location: data?.location,
        venue_name: data?.venue_name,
        category: data?.category,
        status: data?.status,
        maxCapacity: data?.max_capacity,
        isFeatured: data?.is_featured,
        organizer: data?.host?.full_name || 'Event Organizer',
        organizerEmail: data?.host?.email,
        organizerAvatar: data?.host?.avatar_url,
        hostId: data?.host_id,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        tiers: data?.event_tickets?.map(ticket => ({
          id: ticket?.id,
          name: ticket?.ticket_name,
          type: ticket?.ticket_type,
          price: parseFloat(ticket?.price || 0),
          description: ticket?.description,
          quantityAvailable: ticket?.quantity_available,
          quantitySold: ticket?.quantity_sold,
          saleStartDate: ticket?.sale_start_date,
          saleEndDate: ticket?.sale_end_date
        })) || [],
        attendeeCount: data?.event_tickets?.reduce((sum, ticket) => sum + (ticket?.quantity_sold || 0), 0) || 0
      };

      return { data: transformedData, error: null };
    } catch (error) {
      console.error('Unexpected error in getEventById:', error);
      return { data: null, error: error?.message || 'Unexpected error occurred' };
    }
  }

  // Create new event (CREATE operation) - enhanced version
  static async createEvent(eventData, currentUser) {
    try {
      if (!currentUser?.id) {
        return { data: null, error: 'User authentication required' };
      }

      // Validate required fields
      if (!eventData?.title?.trim()) {
        return { data: null, error: 'Event title is required' };
      }

      if (!eventData?.category) {
        return { data: null, error: 'Event category is required' };
      }

      if (!eventData?.date) {
        return { data: null, error: 'Event date is required' };
      }

      // Prepare event data with proper mapping
      const newEvent = {
        title: eventData?.title?.trim(),
        description: eventData?.description?.trim() || null,
        event_date: eventData?.date,
        end_date: eventData?.endDate || null,
        location: eventData?.location?.trim() || null,
        venue_name: eventData?.venueName?.trim() || null,
        category: eventData?.category,
        max_capacity: eventData?.maxCapacity ? parseInt(eventData?.maxCapacity) : null,
        image_url: eventData?.imageUrl || null,
        status: eventData?.status || 'draft',
        is_featured: eventData?.isFeatured || false,
        host_id: currentUser?.id
      };

      const { data, error } = await supabase
        ?.from('events')
        ?.insert([newEvent])
        ?.select()
        ?.single();

      if (error) {
        console.error('Error creating event:', error);
        return { data: null, error: error?.message };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Unexpected error in createEvent:', error);
      return { data: null, error: error?.message || 'Unexpected error occurred' };
    }
  }

  // Create event ticket (new method for ticket creation)
  static async createEventTicket(ticketData) {
    try {
      if (!ticketData?.event_id) {
        return { data: null, error: 'Event ID is required for ticket creation' };
      }

      if (!ticketData?.ticket_name?.trim()) {
        return { data: null, error: 'Ticket name is required' };
      }

      if (!ticketData?.quantity_available || ticketData?.quantity_available <= 0) {
        return { data: null, error: 'Valid ticket quantity is required' };
      }

      // Prepare ticket data
      const newTicket = {
        event_id: ticketData?.event_id,
        ticket_name: ticketData?.ticket_name?.trim(),
        ticket_type: ticketData?.ticket_type || 'free',
        price: ticketData?.price ? parseFloat(ticketData?.price) : 0,
        quantity_available: parseInt(ticketData?.quantity_available),
        quantity_sold: 0,
        description: ticketData?.description?.trim() || null,
        sale_start_date: ticketData?.sale_start_date || new Date()?.toISOString(),
        sale_end_date: ticketData?.sale_end_date || null
      };

      const { data, error } = await supabase
        ?.from('event_tickets')
        ?.insert([newTicket])
        ?.select()
        ?.single();

      if (error) {
        console.error('Error creating event ticket:', error);
        return { data: null, error: error?.message };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Unexpected error in createEventTicket:', error);
      return { data: null, error: error?.message || 'Unexpected error occurred' };
    }
  }

  // Enhanced publishEvent method with complete event and ticket creation
  static async publishEvent(eventFormData, currentUser) {
    try {
      if (!currentUser?.id) {
        return { data: null, error: 'User authentication required' };
      }

      // First, create the event
      const eventData = {
        title: eventFormData?.title,
        description: eventFormData?.description,
        date: eventFormData?.startDate && eventFormData?.startTime ? 
          new Date(`${eventFormData?.startDate}T${eventFormData?.startTime}`)?.toISOString() : null,
        endDate: eventFormData?.endDate && eventFormData?.endTime ? 
          new Date(`${eventFormData?.endDate}T${eventFormData?.endTime}`)?.toISOString() : null,
        location: eventFormData?.venueType === 'physical' ? 
          this.formatPhysicalLocation(eventFormData) : 
          eventFormData?.platform || 'Virtual Event',
        venueName: eventFormData?.venueType === 'physical' ? 
          eventFormData?.venueName : 
          eventFormData?.platform,
        category: eventFormData?.category,
        maxCapacity: eventFormData?.venueType === 'physical' ? 
          parseInt(eventFormData?.capacity) || null : 
          parseInt(eventFormData?.virtualCapacity) || null,
        imageUrl: eventFormData?.image,
        status: eventFormData?.publishStatus === 'published' ? 'published' : 'draft',
        isFeatured: false
      };

      const { data: createdEvent, error: eventError } = await this.createEvent(eventData, currentUser);

      if (eventError || !createdEvent?.id) {
        return { data: null, error: eventError || 'Failed to create event' };
      }

      // Create tickets/tiers
      const createdTickets = [];
      if (eventFormData?.tiers && eventFormData?.tiers?.length > 0) {
        for (const tier of eventFormData?.tiers) {
          const ticketData = {
            event_id: createdEvent?.id,
            ticket_name: tier?.name || 'General Admission',
            ticket_type: tier?.price && parseFloat(tier?.price) > 0 ? 'paid' : 'free',
            price: tier?.price ? parseFloat(tier?.price) : 0,
            quantity_available: tier?.capacity ? parseInt(tier?.capacity) : eventData?.maxCapacity || 100,
            description: tier?.description || '',
            sale_start_date: tier?.earlyBird && tier?.earlyBirdEndDate ? 
              new Date()?.toISOString() : 
              eventData?.date,
            sale_end_date: tier?.earlyBird && tier?.earlyBirdEndDate ? 
              new Date(tier?.earlyBirdEndDate)?.toISOString() : 
              eventData?.date
          };

          const { data: createdTicket, error: ticketError } = await this.createEventTicket(ticketData);
          
          if (createdTicket) {
            createdTickets?.push(createdTicket);
          } else if (ticketError) {
            console.error(`Failed to create ticket for tier ${tier?.name}:`, ticketError);
          }
        }
      }

      return { 
        data: { 
          event: createdEvent, 
          tickets: createdTickets 
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Unexpected error in publishEvent:', error);
      return { data: null, error: error?.message || 'Unexpected error occurred' };
    }
  }

  // Helper method to format physical location
  static formatPhysicalLocation(formData) {
    const parts = [];
    
    if (formData?.address?.trim()) {
      parts?.push(formData?.address?.trim());
    }
    if (formData?.city?.trim()) {
      parts?.push(formData?.city?.trim());
    }
    if (formData?.state?.trim()) {
      parts?.push(formData?.state?.trim());
    }
    if (formData?.zipCode?.trim()) {
      parts?.push(formData?.zipCode?.trim());
    }
    
    return parts?.join(', ') || 'Physical Location';
  }

  // Update existing event (UPDATE operation)
  static async updateEvent(eventId, eventData, currentUser) {
    try {
      if (!currentUser?.id) {
        return { data: null, error: 'User authentication required' };
      }

      // Prepare update data
      const updateData = {
        title: eventData?.title,
        description: eventData?.description,
        event_date: eventData?.event_date || eventData?.startDate,
        end_date: eventData?.end_date || eventData?.endDate,
        location: eventData?.location,
        venue_name: eventData?.venue_name || eventData?.venueName,
        category: eventData?.category,
        max_capacity: eventData?.max_capacity || eventData?.maxCapacity,
        status: eventData?.status || 'draft',
        is_featured: eventData?.is_featured || false,
        // Handle image_url from storage service
        image_url: eventData?.image || eventData?.image_url,
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from('events')?.update(updateData)?.eq('id', eventId)?.eq('host_id', currentUser?.id)?.select()?.single();

      if (error) {
        console.error('Error updating event:', error);
        return { data: null, error: error?.message };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating event:', error);
      return { data: null, error: error?.message || 'Failed to update event' };
    }
  }

  // Delete event (DELETE operation)
  static async deleteEvent(eventId, currentUser) {
    try {
      if (!currentUser?.id) {
        return { success: false, error: 'User authentication required' };
      }

      const { error } = await supabase?.from('events')?.delete()?.eq('id', eventId)?.eq('host_id', currentUser?.id);

      if (error) {
        console.error('Error deleting event:', error);
        return { success: false, error: error?.message };
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('Unexpected error in deleteEvent:', error);
      return { success: false, error: error?.message || 'Unexpected error occurred' };
    }
  }

  // Get events by host (for host dashboard)
  static async getEventsByHost(hostId, filters = {}) {
    try {
      let query = supabase?.from('events')?.select(`
        *,
        host:user_profiles!events_host_id_fkey(
          id,
          full_name,
          email,
          avatar_url
        ),
        event_tickets(
          id,
          ticket_name,
          ticket_type,
          price,
          quantity_available,
          quantity_sold,
          description
        )
      `)?.eq('host_id', hostId);

      // Apply status filter if provided
      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('status', filters?.status);
      }

      // Apply sorting
      query = query?.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching events by host:', error);
        return { data: [], error: error?.message };
      }

      // Transform data
      let transformedData = data?.map(event => ({
        id: event?.id,
        title: event?.title,
        description: event?.description,
        image: event?.image_url,
        date: event?.event_date,
        endDate: event?.end_date,
        location: event?.location,
        venue_name: event?.venue_name,
        category: event?.category,
        status: event?.status,
        maxCapacity: event?.max_capacity,
        isFeatured: event?.is_featured,
        organizer: event?.host?.full_name || 'Event Organizer',
        hostId: event?.host_id,
        createdAt: event?.created_at,
        updatedAt: event?.updated_at,
        tiers: event?.event_tickets?.map(ticket => ({
          id: ticket?.id,
          name: ticket?.ticket_name,
          type: ticket?.ticket_type,
          price: parseFloat(ticket?.price || 0),
          description: ticket?.description,
          quantityAvailable: ticket?.quantity_available,
          quantitySold: ticket?.quantity_sold
        })) || [],
        attendeeCount: event?.event_tickets?.reduce((sum, ticket) => sum + (ticket?.quantity_sold || 0), 0) || 0
      })) || [];

      return { data: transformedData, error: null };
    } catch (error) {
      console.error('Unexpected error in getEventsByHost:', error);
      return { data: [], error: error?.message || 'Unexpected error occurred' };
    }
  }

  // Get event categories with counts - enhanced with fallback
  static async getEventCategories() {
    try {
      const result = await this.withRetry(async () => {
        return await supabase?.from('events')?.select('category')?.eq('status', 'published');
      });

      const { data, error } = result;

      if (error) {
        console.error('Error fetching event categories:', error);
        // Return fallback categories based on database schema
        return { 
          data: {
            all: 0,
            conference: 0,
            workshop: 0,
            networking: 0,
            concert: 0,
            sports: 0,
            exhibition: 0,
            seminar: 0,
            meetup: 0
          }, 
          error: error?.message 
        };
      }

      // Count events by category
      const categoryCounts = data?.reduce((counts, event) => {
        const category = event?.category;
        counts[category] = (counts?.[category] || 0) + 1;
        counts.all = (counts?.all || 0) + 1;
        return counts;
      }, {}) || {};

      return { data: categoryCounts, error: null };
    } catch (error) {
      console.error('Unexpected error in getEventCategories:', error);
      
      // Return fallback categories
      return { 
        data: {
          all: 0,
          conference: 0,
          workshop: 0,
          networking: 0,
          concert: 0,
          sports: 0,
          exhibition: 0,
          seminar: 0,
          meetup: 0
        }, 
        error: error?.message || 'Failed to fetch categories' 
      };
    }
  }

  // Real-time subscription for events
  static subscribeToEvents(callback, filters = {}) {
    try {
      let channel = supabase?.channel('events-changes');

      // Set up the subscription
      channel = channel?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'events',
        filter: filters?.status ? `status=eq.${filters?.status}` : undefined
      }, (payload) => {
        console.log('Event change received:', payload);
        callback?.(payload);
      });

      // Subscribe to the channel
      const subscription = channel?.subscribe();

      // Return unsubscribe function
      return () => {
        supabase?.removeChannel(channel);
      };
    } catch (error) {
      console.error('Error setting up events subscription:', error);
      return () => {}; // Return empty cleanup function
    }
  }

  // Search events with advanced filters - enhanced with retry
  static async searchEvents(searchTerm, filters = {}) {
    try {
      const result = await this.withRetry(async () => {
        let query = supabase?.from('events')?.select(`
          *,
          host:user_profiles!events_host_id_fkey(
            id,
            full_name,
            email,
            avatar_url
          ),
          event_tickets(
            id,
            ticket_name,
            ticket_type,
            price,
            quantity_available,
            quantity_sold,
            description
          )
        `)?.eq('status', 'published');

        // Apply search term
        if (searchTerm) {
          query = query?.or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,location.ilike.%${searchTerm}%`);
        }

        // Apply additional filters
        if (filters?.category && filters?.category !== 'all') {
          query = query?.eq('category', filters?.category);
        }

        if (filters?.location && filters?.location !== 'all') {
          query = query?.ilike('location', `%${filters?.location}%`);
        }

        // Apply date range
        if (filters?.dateRange?.from) {
          query = query?.gte('event_date', filters?.dateRange?.from);
        }
        if (filters?.dateRange?.to) {
          query = query?.lte('event_date', filters?.dateRange?.to);
        }

        // Apply price range filtering
        if (filters?.minPrice !== undefined || filters?.maxPrice !== undefined) {
          // This would require a more complex query with joins to event_tickets
          // For now, we'll fetch all and filter in JavaScript
        }

        // Apply sorting
        query = query?.order('created_at', { ascending: false });

        return await query;
      });

      const { data, error } = result;

      if (error) {
        console.error('Error searching events:', error);
        return { data: [], error: error?.message };
      }

      // Transform and filter by price range if needed
      let transformedData = data?.map(event => ({
        id: event?.id,
        title: event?.title,
        description: event?.description,
        image: event?.image_url,
        date: event?.event_date,
        endDate: event?.end_date,
        location: event?.location,
        venue_name: event?.venue_name,
        category: event?.category,
        status: event?.status,
        maxCapacity: event?.max_capacity,
        isFeatured: event?.is_featured,
        organizer: event?.host?.full_name || 'Event Organizer',
        hostId: event?.host_id,
        createdAt: event?.created_at,
        updatedAt: event?.updated_at,
        tiers: event?.event_tickets?.map(ticket => ({
          id: ticket?.id,
          name: ticket?.ticket_name,
          type: ticket?.ticket_type,
          price: parseFloat(ticket?.price || 0),
          description: ticket?.description,
          quantityAvailable: ticket?.quantity_available,
          quantitySold: ticket?.quantity_sold
        })) || [],
        attendeeCount: event?.event_tickets?.reduce((sum, ticket) => sum + (ticket?.quantity_sold || 0), 0) || 0
      })) || [];

      // Apply client-side price filtering
      if (filters?.priceRange && filters?.priceRange !== 'all') {
        transformedData = transformedData?.filter(event => {
          if (event?.tiers?.length === 0) return filters?.priceRange?.includes('0');
          
          const minPrice = Math.min(...event?.tiers?.map(t => t?.price));
          
          if (filters?.priceRange === '500+') {
            return minPrice >= 500;
          }
          
          const [min, max] = filters?.priceRange?.split('-')?.map(Number);
          return minPrice >= min && minPrice <= max;
        });
      }

      return { data: transformedData, error: null };
    } catch (error) {
      console.error('Unexpected error in searchEvents:', error);
      
      let errorMessage = 'Search failed';
      if (this.isNetworkError(error)) {
        errorMessage = 'Network error during search. Please try again.';
      }
      
      return { data: [], error: errorMessage };
    }
  }
}

export default EventService;